import { useCallback, useEffect, useRef, useState } from "react";
import { Search, X, Shield, Plus, KeyRound, Archive, Pencil, FileText, Banknote, Printer, FileWarning, ArrowRightLeft, Camera } from "lucide-react";
import { Button } from "../components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "../components/ui/alert-dialog";
import { EmptyState } from "../components/EmptyState";
import { listImpounds, archiveJob, saveOwnerLetter, mergeImpoundDuplicate } from "../lib/api";
import OwnerLetterPreviewDialog from "../components/OwnerLetterPreviewDialog";
import { salvageShort, tenantLocale, tenantTz } from "../lib/company";
import TowAuthorityCell from "../components/TowAuthorityCell";
import { useAuth } from "../lib/auth";
import { cn, formatTimeAgo } from "../lib/utils";
import { toast } from "sonner";
import NewImpoundDialog from "../components/NewImpoundDialog";
import ReleaseImpoundDialog from "../components/ReleaseImpoundDialog";
import ReleaseToNtsDialog from "../components/ReleaseToNtsDialog";
import ImpoundDetailSheet, { CountdownPill } from "../components/ImpoundDetailSheet";
import ImpoundProformaDialog from "../components/ImpoundProformaDialog";
import EditImpoundDialog from "../components/EditImpoundDialog";
import OwnerLetterDialog from "../components/OwnerLetterDialog";
import PrintReleaseFormButton from "../components/PrintReleaseFormButton";
import PartPaymentDialog from "../components/PartPaymentDialog";
import { hasCap, can } from "../lib/roles";
import { lastFreeDayIso } from "../lib/tenantTime";

const fmtMoney = (n) =>
  n == null ? "—" : `$${Number(n).toLocaleString(tenantLocale(), { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const fmtDate = (iso) =>
  iso ? new Date(iso).toLocaleString(tenantLocale(), { timeZone: tenantTz(), dateStyle: "medium", timeStyle: "short", hourCycle: "h23" }) : "—";

/** Status badge derives from 3 timestamps. */
function deriveStatus(j) {
  if (j.archived_at) return { label: "Archived", cls: "bg-slate-200 text-slate-700 dark:bg-slate-700 dark:text-slate-200" };
  if (j.released_to_nts_at) return { label: `To ${salvageShort()}`, cls: "bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-200" };
  if (j.released_at) return { label: "Released", cls: "bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-200" };
  return { label: "In yard", cls: "bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-200" };
}

const POLL_MS = 30000;

export default function ImpoundsPage() {
  const { user } = useAuth();
  const role = user?.role;
  const canCreate = hasCap(user, "admin", "dispatcher");
  // Sep 2026 — viewer retired; dispatcher absorbed billing capabilities.
  const canLetter = canCreate;
  const canArchive = hasCap(user, "admin", "dispatcher") && can(user, "archive_jobs");
  const canSetTowAuthority = hasCap(user, "admin", "dispatcher");

  const [query, setQuery] = useState(() => {
    // Hydrate from ?q=... so the global top-bar search lands directly into the filter.
    if (typeof window !== "undefined") {
      const p = new URLSearchParams(window.location.search);
      return p.get("q") || "";
    }
    return "";
  });
  const [status, setStatus] = useState("active");
  const [term, setTerm] = useState("");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createOpen, setCreateOpen] = useState(false);
  const [releaseJob, setReleaseJob] = useState(null);
  // Snapshot of a freshly saved owner letter, shown for checking before print.
  const [autoLetter, setAutoLetter] = useState(null);
  const [earlyReleaseJob, setEarlyReleaseJob] = useState(null);
  const [ntsJob, setNtsJob] = useState(null);
  const [archiveTarget, setArchiveTarget] = useState(null);
  const [detailJob, setDetailJob] = useState(null);
  const [editJob, setEditJob] = useState(null);
  const [letterJob, setLetterJob] = useState(null);
  const [payJob, setPayJob] = useState(null);
  const [proformaJob, setProformaJob] = useState(null);
  // Sep 2026 — impound duplicate merge. `mergeTarget` = { keep, drop }
  // when the dispatcher confirms via the "Merge dup" button. Duplicate
  // detection is client-side: any two active rows with the same rego.
  const [mergeTarget, setMergeTarget] = useState(null);
  const [merging, setMerging] = useState(false);
  const duplicateRegos = (() => {
    const counts = new Map();
    for (const r of rows) {
      if (r.released_at || r.released_to_nts_at) continue;
      const k = (r.rego || "").trim().toUpperCase();
      if (!k) continue;
      counts.set(k, (counts.get(k) || 0) + 1);
    }
    return new Set([...counts.entries()].filter(([, n]) => n > 1).map(([k]) => k));
  })();

  // Stale-response guard: drops overlapping in-flight requests so a slow
  // earlier fetch (broader filter) can't overwrite a fresh narrower result.
  // Fixes the "typed search briefly matches, then list snaps back" race
  // (Aug 2026 sweep — same pattern as YardStockPage + HistoryPage).
  const reqIdRef = useRef(0);

  const refresh = useCallback(async (showSpinner = false) => {
    const myReqId = ++reqIdRef.current;
    if (showSpinner) setLoading(true);
    try {
      const params = { status };
      if (query.trim()) params.q = query.trim();
      if (term) params.term = term;
      const data = await listImpounds(params);
      if (myReqId !== reqIdRef.current) return;
      setRows(data);
    } catch {
      if (myReqId !== reqIdRef.current) return;
      toast.error("Failed to load impounds");
    } finally {
      // Clear on the LATEST request regardless of who set it: a silent
      // refetch overtaking a spinner request would otherwise leave the
      // spinner up forever (seen on Storage Yards, Oct 2026).
      if (myReqId === reqIdRef.current) setLoading(false);
    }
  }, [query, status, term]);

  // Debounced reload on filter change + initial load (with spinner).
  useEffect(() => {
    const id = setTimeout(() => refresh(true), 250);
    return () => clearTimeout(id);
  }, [refresh]);

  // Live polling — silent refresh every 30s so the billing view stays in sync.
  useEffect(() => {
    const t = setInterval(() => refresh(false), POLL_MS);
    const onFocus = () => refresh(false);
    window.addEventListener("focus", onFocus);
    return () => { clearInterval(t); window.removeEventListener("focus", onFocus); };
  }, [refresh]);

  const onArchive = async () => {
    if (!archiveTarget) return;
    try {
      await archiveJob(archiveTarget.id);
      toast.success("Impound archived");
      setArchiveTarget(null);
      refresh(true);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Archive failed");
    }
  };

  // Feb 2026 — printable "Stock Take" sheet for impounds. Mirrors the
  // yard-stock print (see YardStockPage.jsx) — same A4 layout, tick
  // column, header/footer. Fetches ONLY active in-yard impounds
  // (ignoring current search/term/status filters) so the walk-the-yard
  // count is complete. Groups by 28-day vs 6-month term, alphabetically
  // by rego within each group.
  const [printingStockTake, setPrintingStockTake] = useState(false);
  const onPrintStockTake = useCallback(async () => {
    setPrintingStockTake(true);
    try {
      const data = await listImpounds({ status: "active" });
      const groups = {
        "28d": { label: "28-Day Impounds", rows: [] },
        "6mo": { label: "6-Month Impounds", rows: [] },
        other: { label: "Unspecified term", rows: [] },
      };
      for (const r of data) {
        const key = r.impound_term === "28d" ? "28d" : r.impound_term === "6mo" ? "6mo" : "other";
        groups[key].rows.push(r);
      }
      const order = ["28d", "6mo", "other"];
      const now = new Date();
      const stampNz = new Intl.DateTimeFormat(tenantLocale(), {
        weekday: "long", day: "2-digit", month: "long", year: "numeric",
        hour: "2-digit", minute: "2-digit", hour12: false,
        timeZone: tenantTz(),
      }).format(now);
      const dateOnly = new Intl.DateTimeFormat(tenantLocale(), {
        day: "2-digit", month: "2-digit", year: "numeric",
        timeZone: tenantTz(),
      }).format(now);
      const esc = (s) => String(s ?? "").replace(/[&<>"']/g, (c) => (
        { "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c]
      ));
      const fmtDateShort = (iso) => {
        if (!iso) return "—";
        try {
          return new Intl.DateTimeFormat(tenantLocale(), {
            day: "2-digit", month: "short", year: "numeric",
            timeZone: tenantTz(),
          }).format(new Date(iso));
        } catch { return "—"; }
      };
      const sections = order
        .filter((k) => groups[k].rows.length > 0)
        .map((k) => {
          // Alphabetical by rego so a dispatcher walking the yard can
          // tick vehicles off in the order they encounter them — same
          // pattern as the yard-stock print.
          const rowsForTerm = [...groups[k].rows].sort((a, b) =>
            (a.rego || "").localeCompare(b.rego || "", "en", { sensitivity: "base", numeric: true }),
          );
          const rowsHtml = rowsForTerm.map((r, i) => `
            <tr>
              <td class="num">${i + 1}</td>
              <td class="mono">${esc(r.rego || "—")}</td>
              <td>${esc(r.vehicle || "—")}</td>
              <td>${esc(r.customer_name || r.impound_owner_name || "—")}</td>
              <td>${esc(fmtDateShort(r.impounded_at))}</td>
              <td>${esc(fmtDateShort(lastFreeDayIso(r.impound_release_due_at)))}</td>
              <td class="tick"></td>
            </tr>`).join("");
          return `
            <section class="yard">
              <h2>${esc(groups[k].label)}
                <span class="count">${rowsForTerm.length} vehicle${rowsForTerm.length === 1 ? "" : "s"}</span>
              </h2>
              <table>
                <thead>
                  <tr>
                    <th class="num">#</th>
                    <th>REGO</th>
                    <th>Vehicle</th>
                    <th>Driver</th>
                    <th>Impounded</th>
                    <th>Release Due</th>
                    <th class="tick">✓</th>
                  </tr>
                </thead>
                <tbody>${rowsHtml}</tbody>
              </table>
            </section>`;
        }).join("");
      const html = `<!doctype html>
<html lang="en"><head>
<meta charset="utf-8" />
<title>Impound Stock Take — ${esc(dateOnly)}</title>
<style>
  @page { size: A4; margin: 10mm 10mm 8mm 10mm; }
  * { box-sizing: border-box; }
  html, body { margin: 0; padding: 0; font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif; color: #0f172a; }
  header { display: flex; align-items: baseline; justify-content: space-between; border-bottom: 2px solid #0f172a; padding-bottom: 4px; margin-bottom: 8px; }
  header h1 { margin: 0; font-size: 18px; letter-spacing: -0.01em; }
  header .stamp { font-size: 10px; color: #475569; }
  .summary { font-size: 10px; color: #475569; margin-bottom: 10px; }
  section.yard { margin-bottom: 10px; }
  section.yard h2 { margin: 0 0 3px; font-size: 13px; text-transform: uppercase; letter-spacing: 0.04em; color: #0f172a; border-bottom: 1px solid #cbd5e1; padding-bottom: 2px; display: flex; align-items: baseline; gap: 10px; break-after: avoid; }
  section.yard h2 .count { font-size: 10px; color: #64748b; font-weight: 500; text-transform: none; letter-spacing: 0; }
  table { width: 100%; border-collapse: collapse; font-size: 10.5px; }
  thead th { background: #f1f5f9; text-align: left; font-weight: 700; text-transform: uppercase; letter-spacing: 0.03em; font-size: 9px; color: #334155; padding: 4px 8px; border-bottom: 1px solid #cbd5e1; }
  tbody td { padding: 5px 8px; border-bottom: 1px solid #e2e8f0; vertical-align: middle; }
  tbody tr:nth-child(even) td { background: #f8fafc; }
  td.num, th.num { width: 24px; color: #64748b; text-align: right; font-variant-numeric: tabular-nums; }
  td.mono { font-family: ui-monospace, SFMono-Regular, Menlo, monospace; font-weight: 700; letter-spacing: 0.01em; }
  td.tick, th.tick { width: 26px; text-align: center; }
  td.tick { border: 1px solid #94a3b8; border-left: none; border-right: 1px solid #e2e8f0; }
  td.tick::before { content: ""; display: inline-block; width: 12px; height: 12px; border: 1.5px solid #64748b; border-radius: 2px; vertical-align: middle; }
  footer { margin-top: 10px; padding-top: 4px; border-top: 1px solid #cbd5e1; font-size: 9.5px; color: #64748b; display: flex; justify-content: space-between; }
  .empty { text-align: center; padding: 40px 0; color: #94a3b8; font-style: italic; }
  @media print {
    tr { break-inside: avoid; page-break-inside: avoid; }
    thead { display: table-header-group; }
    tfoot { display: table-footer-group; }
    footer { break-inside: avoid; page-break-inside: avoid; }
    .no-print { display: none !important; }
  }
  .no-print { position: fixed; top: 12px; right: 12px; }
  .no-print button { padding: 8px 16px; font-size: 13px; border: 1px solid #0f172a; background: #0f172a; color: white; border-radius: 4px; cursor: pointer; font-weight: 600; }
  .no-print button.secondary { background: white; color: #0f172a; margin-right: 6px; }
</style>
</head><body>
  <div class="no-print">
    <button class="secondary" onclick="window.close()">Close</button>
    <button onclick="window.print()">Print</button>
  </div>
  <header>
    <h1>Impound Stock Take</h1>
    <div class="stamp">${esc(stampNz)}</div>
  </header>
  <div class="summary">Total vehicles impounded (in yard): <strong>${data.length}</strong> · Walk the yard and tick each vehicle you locate.</div>
  ${sections || `<div class="empty">No impounded vehicles currently in the yard.</div>`}
  <footer>
    <span>Verified by: __________________________</span>
    <span>Signature / Date: __________________________</span>
  </footer>
  <script>setTimeout(() => window.print(), 300);</script>
</body></html>`;
      const w = window.open("", "_blank", "width=900,height=1100");
      if (!w) {
        toast.error("Pop-up blocked — please allow pop-ups for this site.");
        return;
      }
      w.document.open();
      w.document.write(html);
      w.document.close();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load stock take");
    } finally {
      setPrintingStockTake(false);
    }
  }, []);

  // Oct 2026: there is no actions column any more. Up to eight buttons were
  // crammed into one table cell, which is what made this list unreadable;
  // clicking a row opens the detail panel and every action lives there.

  return (
    <div className="space-y-4" data-testid="impounds-page">
      <div className="space-y-3">
        <div>
          <h1 className="font-display text-3xl font-black tracking-tight text-slate-900 dark:text-slate-100 flex items-center gap-2">
            <Shield className="h-7 w-7 text-blue-700 dark:text-blue-300" />
            Police Impounds
          </h1>
          <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">
            Searchable database of police impound jobs · created manually by dispatch
          </p>
        </div>
        {canCreate && (
          <div className="flex justify-center">
            <Button
              variant="outline"
              onClick={() => setCreateOpen(true)}
              size="lg"
              className="font-semibold px-8"
              data-testid="impounds-new-btn"
            >
              <Plus className="h-5 w-5.5" /> New Impound
            </Button>
          </div>
        )}
      </div>

      {/* Filters bar */}
      <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-md p-3 flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[260px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
          <input
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search rego, vehicle, colour, owner, phone, notice #, claim #, address…"
            className="w-full pl-9 pr-9 py-2 bg-slate-50 dark:bg-slate-800 dark:text-slate-100 border border-slate-200 dark:border-slate-700 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            data-testid="impounds-search"
          />
          {query && (
            <button
              onClick={() => setQuery("")}
              className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-700 dark:hover:text-slate-200"
            >
              <X className="h-4 w-4" />
            </button>
          )}
        </div>
        <Select value={status} onValueChange={setStatus}>
          <SelectTrigger className="w-[180px]" data-testid="impounds-status-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="active">In yard (active)</SelectItem>
            <SelectItem value="released">Released to Owner</SelectItem>
            <SelectItem value="released_to_nts">Signed Over</SelectItem>
            <SelectItem value="archived">Archived</SelectItem>
            <SelectItem value="all">All</SelectItem>
          </SelectContent>
        </Select>
        <Select value={term || "any"} onValueChange={(v) => setTerm(v === "any" ? "" : v)}>
          <SelectTrigger className="w-[140px]" data-testid="impounds-term-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="any">Any term</SelectItem>
            <SelectItem value="28d">28 days</SelectItem>
            <SelectItem value="6mo">6 months</SelectItem>
          </SelectContent>
        </Select>
        <span className="text-xs text-slate-500 dark:text-slate-400">
          {loading ? "Loading…" : `${rows.length} record${rows.length === 1 ? "" : "s"}`}
        </span>
        <div className="flex-1" />
        <Button
          size="sm"
          variant="outline"
          onClick={onPrintStockTake}
          disabled={printingStockTake}
          className="font-semibold"
          title="Print an A4 stock-take sheet grouped by 28-day / 6-month term"
          data-testid="impounds-stock-take-btn"
        >
          <Printer className="h-4 w-4.5" />
          {printingStockTake ? "Loading…" : "Stock Take"}
        </Button>
      </div>

      {/* Table */}
      <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-md overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 dark:bg-slate-800 border-b border-slate-200 dark:border-slate-700 text-[10px] uppercase tracking-wider text-slate-500 dark:text-slate-400">
            <tr>
              <th className="text-left px-3 py-2 font-bold">Impounded</th>
              <th className="text-left px-3 py-2 font-bold">Rego / Vehicle</th>
              <th className="text-left px-3 py-2 font-bold">Driver</th>
              <th className="text-left px-3 py-2 font-bold">Term</th>
              <th className="text-left px-3 py-2 font-bold">Release Due / Countdown</th>
              <th className="text-left px-3 py-2 font-bold whitespace-nowrap">Current Total</th>
              <th className="text-left px-3 py-2 font-bold">Notice #</th>
              {canSetTowAuthority && <th className="text-left px-3 py-2 font-bold whitespace-nowrap">Tow Authority #</th>}
              <th className="text-left px-3 py-2 font-bold">Status</th>
            </tr>
          </thead>
          <tbody data-testid="impounds-rows">
            {loading && <tr><td colSpan={(canSetTowAuthority ? 1 : 0) + 8} className="text-center text-slate-500 py-12">Loading…</td></tr>}
            {!loading && rows.length === 0 && (
              <tr><td colSpan={(canSetTowAuthority ? 1 : 0) + 8} className="p-0">
                <EmptyState
                  icon={Shield}
                  title={query || status !== "active" || term ? "No impounds match" : "No police impounds yet"}
                  hint={query || status !== "active" || term ? "Adjust the search, status, or term filter to see more results." : "Manually created police impound jobs will appear here — click New Impound to add the first one."}
                  testId="impounds-empty"
                />
              </td></tr>
            )}
            {!loading && rows.map((j) => {
              const total = j.pricing_breakdown?.total;
              const s = deriveStatus(j);
              const isClosed = j.released_at || j.released_to_nts_at;
              return (
                <tr
                  key={j.id}
                  onClick={() => setDetailJob(j)}
                  className="border-b border-slate-100 dark:border-slate-800 hover:bg-slate-50 dark:hover:bg-slate-800/50 transition-colors cursor-pointer"
                  data-testid={`impound-row-${j.id}`}
                  title="Click for full impound details"
                >
                  <td className="px-3 py-2 text-slate-700 dark:text-slate-300 whitespace-nowrap">
                    <div className="font-semibold">{fmtDate(j.impounded_at)}</div>
                    <div className="text-xs text-slate-400">{formatTimeAgo(j.impounded_at || j.created_at)}</div>
                  </td>
                  <td className="px-3 py-2">
                    <div className="font-semibold text-slate-900 dark:text-slate-100 font-mono flex items-center gap-1.5">
                      {j.rego || "—"}
                      {(j.photos?.length || 0) > 0 && (
                        <span
                          className="inline-flex items-center gap-0.5 rounded bg-amber-100 px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wider text-amber-700 dark:bg-amber-900/40 dark:text-amber-200"
                          title={`${j.photos.length} photo${j.photos.length === 1 ? "" : "s"} — open the vehicle to view them`}
                          data-testid={`impound-photos-badge-${j.id}`}
                        >
                          <Camera className="h-2.5 w-2.5" /> {j.photos.length}
                        </span>
                      )}
                    </div>
                    <div className="text-xs text-slate-500 dark:text-slate-400">{j.vehicle}</div>
                    {j.gvm_kg != null && (
                      <div className="text-[10px] uppercase tracking-wider text-slate-400">{j.gvm_kg}kg · {j.gvm_kg <= 3500 ? "light" : "heavy"}</div>
                    )}
                  </td>
                  <td className="px-3 py-2 text-slate-700 dark:text-slate-300">
                    <div>{j.customer_name || j.impound_owner_name || "—"}</div>
                    <div className="text-xs text-slate-500">{j.customer_phone || j.impound_owner_phone}</div>
                  </td>
                  <td className="px-3 py-2 text-slate-700 dark:text-slate-300">{j.impound_term === "28d" ? "28 days" : j.impound_term === "6mo" ? "6 months" : "—"}</td>
                  <td className="px-3 py-2 text-slate-700 dark:text-slate-300 whitespace-nowrap">
                    <div>{j.impound_release_due_at
                        ? `${new Date(lastFreeDayIso(j.impound_release_due_at)).toLocaleDateString(tenantLocale(), { timeZone: tenantTz(), dateStyle: "medium" })} (end of day)`
                        : "—"}</div>
                    {!isClosed && <div className="mt-1"><CountdownPill job={j} compact /></div>}
                  </td>
                  <td className="px-3 py-2 text-left whitespace-nowrap">
                    <div className="text-slate-900 dark:text-slate-100 font-mono font-semibold">{fmtMoney(total)}</div>
                    {(j.part_payments || []).length > 0 && (() => {
                      const paid = j.part_payments.reduce((a, p) => a + (Number(p.amount) || 0), 0);
                      return (
                        <div className="text-[10px] font-mono text-emerald-700 dark:text-emerald-300" data-testid={`impound-partpaid-${j.id}`}>
                          paid {fmtMoney(paid)}{total != null ? ` · bal ${fmtMoney(Math.max(0, total - paid))}` : ""}
                        </div>
                      );
                    })()}
                  </td>
                  <td className="px-3 py-2 text-slate-600 dark:text-slate-400 text-xs font-mono">
                    <div>{j.impound_notice_number || j.police_reference || "—"}</div>
                  </td>
                  {canSetTowAuthority && (
                    <td className="px-3 py-2" onClick={(e) => e.stopPropagation()}>
                      <TowAuthorityCell
                        job={j}
                        disabled={!!j.archived_at}
                        testIdPrefix="impound-tow-authority"
                        onSaved={(updated) =>
                          setRows((rs) => rs.map((r) => (r.id === updated.id ? { ...r, ...updated } : r)))
                        }
                      />
                    </td>
                  )}
                  <td className="px-3 py-2">
                    <span className={cn("text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded", s.cls)}>{s.label}</span>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <NewImpoundDialog
        open={createOpen}
        onOpenChange={setCreateOpen}
        onCreated={async (job) => {
          refresh(true);
          if (!job) return;
          // Auto-print the first owner letter (June 2026). Rewritten July
          // 2026 with defensive branching after intermittent "sometimes
          // prints" reports: every path shows a toast so we never fail
          // silently and always give the user a manual retry button.
          if (!job.impound_owner_name || !job.impound_owner_address) {
            toast.warning("Owner details missing — letter not printed. Add owner name + address on the job, then print from the Owner Letter dialog.", { duration: 8000 });
            return;
          }
          try {
            const letter = await saveOwnerLetter(job.id, { note: "Auto-printed on impound creation" });
            // Nov 2026 — show it on screen first instead of firing the
            // printer blind, so the owner's name and address can be checked
            // against the paperwork before anything is posted. Printing from
            // the preview runs its own toasts, so we stay quiet here.
            setAutoLetter(letter.snapshot);
          } catch (e) {
            toast.error(e?.response?.data?.detail || "Failed to prepare owner letter", {
              duration: 10000,
              action: { label: "Retry", onClick: async () => {
                try {
                  const letter = await saveOwnerLetter(job.id, { note: "Manual retry after auto-print failure" });
                  setAutoLetter(letter.snapshot);
                } catch (err) {
                  toast.error(err?.response?.data?.detail || "Retry also failed — try the Owner Letter dialog on the impound row.");
                }
              } },
            });
          }
        }}
      />
      <OwnerLetterPreviewDialog
        snapshot={autoLetter}
        open={!!autoLetter}
        onOpenChange={(o) => !o && setAutoLetter(null)}
      />
      <ReleaseImpoundDialog
        job={releaseJob}
        open={!!releaseJob}
        onOpenChange={(o) => !o && setReleaseJob(null)}
        onReleased={() => refresh(true)}
      />
      <ReleaseImpoundDialog
        job={earlyReleaseJob}
        open={!!earlyReleaseJob}
        onOpenChange={(o) => !o && setEarlyReleaseJob(null)}
        onReleased={() => refresh(true)}
        mode="early"
      />
      <ReleaseToNtsDialog
        job={ntsJob}
        open={!!ntsJob}
        onOpenChange={(o) => !o && setNtsJob(null)}
        onReleased={() => refresh(true)}
      />
      <ImpoundDetailSheet
        job={detailJob}
        open={!!detailJob}
        onOpenChange={(o) => !o && setDetailJob(null)}
        role={role}
        onRelease={(j) => setReleaseJob(j)}
        onReleaseToNts={(j) => setNtsJob(j)}
        onArchive={(j) => setArchiveTarget(j)}
        onEdit={(j) => setEditJob(j)}
        onLetter={(j) => setLetterJob(j)}
        onPartPay={(j) => setPayJob(j)}
        onEarlyRelease={(j) => setEarlyReleaseJob(j)}
        onEmailProforma={(j) => setProformaJob(j)}
        hasDuplicate={!!detailJob && duplicateRegos.has((detailJob.rego || "").trim().toUpperCase())}
        onMergeDuplicate={(j) => {
          const rego = (j.rego || "").trim().toUpperCase();
          const other = rows.find(
            (r) => r.id !== j.id
              && !r.released_at && !r.released_to_nts_at
              && (r.rego || "").trim().toUpperCase() === rego,
          );
          if (!other) { toast.error("Couldn't find the duplicate row"); return; }
          setMergeTarget({ keep: j, drop: other });
        }}
      />
      <ImpoundProformaDialog
        job={proformaJob}
        open={!!proformaJob}
        onOpenChange={(o) => !o && setProformaJob(null)}
      />
      <EditImpoundDialog
        impound={editJob}
        open={!!editJob}
        onOpenChange={(o) => !o && setEditJob(null)}
        onSaved={(updated) => {
          setRows((rs) => rs.map((r) => (r.id === updated.id ? { ...r, ...updated } : r)));
          if (detailJob && detailJob.id === updated.id) setDetailJob({ ...detailJob, ...updated });
          setEditJob(null);
        }}
      />
      <OwnerLetterDialog
        jobId={letterJob?.id}
        open={!!letterJob}
        onOpenChange={(o) => !o && setLetterJob(null)}
      />
      <PartPaymentDialog
        job={payJob}
        open={!!payJob}
        onOpenChange={(o) => !o && setPayJob(null)}
        role={role}
        onSaved={() => refresh(false)}
      />

      <AlertDialog open={!!archiveTarget} onOpenChange={(o) => !o && setArchiveTarget(null)}>
        <AlertDialogContent data-testid="impound-archive-confirm">
          <AlertDialogHeader>
            <AlertDialogTitle>Archive this impound?</AlertDialogTitle>
            <AlertDialogDescription>
              {archiveTarget?.rego || archiveTarget?.vehicle} will be hidden from the default list. All data + receipt are preserved and can be unarchived by an admin.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="impound-archive-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={onArchive} data-testid="impound-archive-confirm-btn">Archive</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={!!mergeTarget} onOpenChange={(o) => !o && !merging && setMergeTarget(null)}>
        <AlertDialogContent data-testid="impound-merge-confirm">
          <AlertDialogHeader>
            <AlertDialogTitle>Merge duplicate impound?</AlertDialogTitle>
            <AlertDialogDescription>
              Two active impounds exist for <span className="font-mono font-semibold">{mergeTarget?.keep?.rego}</span>.
              This will keep the row you clicked and fold the other one's data into it — any photos, part-payments, owner-letters and sent emails on the duplicate will be preserved on the keeper. The duplicate row is then deleted. This cannot be undone.
              <div className="mt-3 grid grid-cols-2 gap-2 text-xs">
                <div className="border border-emerald-400/60 rounded p-2 bg-emerald-50 dark:bg-emerald-950/20">
                  <div className="font-bold uppercase text-emerald-700 dark:text-emerald-400 mb-1">Keep</div>
                  <div>{mergeTarget?.keep?.impound_owner_name || mergeTarget?.keep?.customer_name || "—"}</div>
                  <div className="text-slate-500">{mergeTarget?.keep?.vehicle || "—"}</div>
                  <div className="text-slate-500 text-[10px]">Created {mergeTarget?.keep?.created_at ? formatTimeAgo(mergeTarget.keep.created_at) : "—"}</div>
                </div>
                <div className="border border-red-400/60 rounded p-2 bg-red-50 dark:bg-red-950/20">
                  <div className="font-bold uppercase text-red-700 dark:text-red-400 mb-1">Delete</div>
                  <div>{mergeTarget?.drop?.impound_owner_name || mergeTarget?.drop?.customer_name || "—"}</div>
                  <div className="text-slate-500">{mergeTarget?.drop?.vehicle || "—"}</div>
                  <div className="text-slate-500 text-[10px]">Created {mergeTarget?.drop?.created_at ? formatTimeAgo(mergeTarget.drop.created_at) : "—"}</div>
                </div>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={merging} data-testid="impound-merge-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={async (e) => {
                e.preventDefault();
                if (!mergeTarget) return;
                setMerging(true);
                try {
                  const fresh = await mergeImpoundDuplicate(mergeTarget.keep.id, mergeTarget.drop.id);
                  setRows((rs) => rs.filter((r) => r.id !== mergeTarget.drop.id)
                                  .map((r) => (r.id === fresh.id ? { ...r, ...fresh } : r)));
                  toast.success("Duplicate merged");
                  setMergeTarget(null);
                } catch (err) {
                  toast.error(err?.response?.data?.detail || "Merge failed");
                } finally {
                  setMerging(false);
                }
              }}
              data-testid="impound-merge-confirm-btn"
            >
              {merging ? "Merging…" : "Merge & delete duplicate"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
